import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, RefreshCw } from "lucide-react";
import { api } from "../lib/api";
import { useSeo } from "../lib/useSeo";
import { Button } from "../components/ui/button";

const AdminAnalyticsDiagnostics = () => {
  useSeo({ title: "Analytics Diagnostics", description: "Inspecteer recente pageview-events" });
  const [data, setData] = useState(null);
  const [hours, setHours] = useState(24);
  const [includeBots, setIncludeBots] = useState(false);
  const [includeInternal, setIncludeInternal] = useState(false);
  const [err, setErr] = useState("");

  const load = () => {
    setData(null);
    setErr("");
    const qs = new URLSearchParams({
      hours: String(hours),
      limit: "200",
      include_bots: includeBots ? "true" : "false",
      include_internal: includeInternal ? "true" : "false",
    });
    api(`/admin/analytics/diagnostics?${qs.toString()}`)
      .then(setData)
      .catch((e) => setErr(e.message));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [hours, includeBots, includeInternal]);

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
      <div className="flex items-center justify-between">
        <div>
          <Button asChild variant="ghost" size="sm" className="rounded-xl" data-testid="back-to-admin">
            <Link to="/admin"><ArrowLeft size={16} className="mr-1" /> Admin</Link>
          </Button>
          <h1 className="mt-2 font-display text-3xl font-extrabold text-navy">Analytics diagnostics</h1>
          <p className="mt-1 text-sm text-slate-500">Inspecteer recente pageview-events om tracking-anomalieën te vinden.</p>
        </div>
        <Button variant="outline" onClick={load} className="rounded-xl" data-testid="refresh-diagnostics">
          <RefreshCw size={16} className="mr-2" /> Ververs
        </Button>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <label className="text-sm text-slate-600">Window:</label>
        <select
          value={hours}
          onChange={(e) => setHours(parseInt(e.target.value, 10))}
          className="rounded-xl border border-border bg-white px-3 py-1.5 text-sm"
          data-testid="window-select"
        >
          <option value={1}>1 uur</option>
          <option value={6}>6 uur</option>
          <option value={24}>24 uur</option>
          <option value={72}>3 dagen</option>
          <option value={168}>7 dagen</option>
        </select>
        <label className="flex items-center gap-1 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={includeBots}
            onChange={(e) => setIncludeBots(e.target.checked)}
            data-testid="include-bots"
          />{" "}
          Toon bots
        </label>
        <label className="flex items-center gap-1 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={includeInternal}
            onChange={(e) => setIncludeInternal(e.target.checked)}
            data-testid="include-internal"
          />{" "}
          Toon /admin
        </label>
      </div>

      {err && <div className="mt-6 rounded-xl bg-rose-50 p-3 text-sm text-rose-700" data-testid="diag-error">{err}</div>}

      {!data ? (
        <div className="mt-20 flex justify-center">
          <Loader2 className="animate-spin text-brandblue" size={32} />
        </div>
      ) : (
        <>
          <div className="mt-6 grid gap-3 sm:grid-cols-4">
            <Stat label="Totaal screen_views" value={data.total_screen_views_in_window} />
            <Stat label="Bots gevonden" value={data.bots_in_window} tint="text-rose-600" />
            <Stat label="/admin-paden" value={data.internal_in_window} tint="text-amber-600" />
            <Stat label="Menselijk (netto)" value={data.human_in_window} tint="text-success" />
          </div>

          {data.tracking_boundary && (
            <p className="mt-4 text-xs text-slate-400">
              Tracking verbeterd vanaf {new Date(data.tracking_boundary).toLocaleString("nl-NL")}.
            </p>
          )}

          <div className="mt-6 overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-left uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2">Tijd</th>
                  <th className="px-3 py-2">Visitor</th>
                  <th className="px-3 py-2">Pad</th>
                  <th className="px-3 py-2">Referrer</th>
                  <th className="px-3 py-2">Device</th>
                  <th className="px-3 py-2">Browser</th>
                  <th className="px-3 py-2">Bot</th>
                  <th className="px-3 py-2">Admin</th>
                  <th className="px-3 py-2">Sessie</th>
                  <th className="px-3 py-2">User?</th>
                  <th className="px-3 py-2">UA</th>
                </tr>
              </thead>
              <tbody>
                {(data.rows || []).map((r, i) => (
                  <tr key={i} className="border-t border-border" data-testid={`diag-row-${i}`}>
                    <td className="whitespace-nowrap px-3 py-1.5 text-slate-500">
                      {r.ts ? new Date(r.ts).toLocaleTimeString("nl-NL") : "—"}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-slate-700">{r.visitor_hash || "—"}</td>
                    <td className="px-3 py-1.5 font-medium text-navy">{r.pathname}</td>
                    <td className="px-3 py-1.5 text-slate-500">{r.referrer || "—"}</td>
                    <td className="px-3 py-1.5 text-slate-500">{r.device}/{r.os}</td>
                    <td className="px-3 py-1.5 text-slate-500">{r.browser}</td>
                    <td className="px-3 py-1.5">
                      {r.is_bot === true && <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-semibold text-rose-700">BOT</span>}
                      {r.is_bot === false && <span className="text-slate-400">nee</span>}
                      {r.is_bot == null && <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-1.5">
                      {r.is_internal === true && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">ADMIN</span>}
                      {r.is_internal === false && <span className="text-slate-400">nee</span>}
                      {r.is_internal == null && <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-slate-400">{r.session_id}</td>
                    <td className="px-3 py-1.5 text-slate-500">{r.has_user ? "ja" : "—"}</td>
                    <td className="max-w-[260px] truncate px-3 py-1.5 text-slate-400" title={r.ua_short}>{r.ua_short}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(!data.rows || data.rows.length === 0) && (
            <p className="mt-6 text-center text-sm text-slate-400">Geen events in het geselecteerde venster.</p>
          )}
        </>
      )}
    </div>
  );
};

const Stat = ({ label, value, tint = "text-navy" }) => (
  <div className="rounded-2xl border border-border bg-white p-4">
    <div className={`font-display text-2xl font-extrabold ${tint}`}>{value}</div>
    <div className="mt-0.5 text-xs text-slate-500">{label}</div>
  </div>
);

export default AdminAnalyticsDiagnostics;
